import type React from 'react';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

/**
 * Hook to track image load state and invoke callback when image has loaded.
 * The callback receives a React.SyntheticEvent<HTMLImageElement> to match the
 * next/image onLoad API so BaseImage can be used as a drop-in replacement.
 *
 * @param onLoad - Optional callback invoked when the image has loaded and decoded
 * @returns Ref to attach to the img element
 *
 */
export function useImageLoad(onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void) {
  const ref = useRef<HTMLImageElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    const img = ref.current;
    if (!img || !onLoad) return;

    let cancelled = false;

    const notify = () => {
      if (!cancelled) {
        // Construct a synthetic-event-compatible object so consumers can use
        // e.target (matching next/image's onLoad API) without a cast.
        const syntheticEvent = {
          target: img,
          currentTarget: img,
          nativeEvent: new Event('load'),
          bubbles: false,
          cancelable: false,
          defaultPrevented: false,
          eventPhase: 0,
          isTrusted: true,
          preventDefault: () => undefined,
          isDefaultPrevented: () => false,
          stopPropagation: () => undefined,
          isPropagationStopped: () => false,
          persist: () => undefined,
          timeStamp: Date.now(),
          type: 'load',
        } as unknown as React.SyntheticEvent<HTMLImageElement>;
        onLoad(syntheticEvent);
      }
    };

    // Image already loaded
    if (img.complete && img.naturalWidth > 0) {
      // Ensure decode has completed if supported
      if ('decode' in img) {
        img.decode().then(notify).catch(notify);
      } else {
        notify();
      }
      return () => {
        cancelled = true;
      };
    }

    // Not loaded yet → wait for both load and decode before notifying.
    const onLoadEvent = () => {
      if ('decode' in img) {
        img.decode().then(notify).catch(notify);
      } else {
        notify();
      }
    };

    img.addEventListener('load', onLoadEvent, { once: true });

    return () => {
      cancelled = true;
      img.removeEventListener('load', onLoadEvent);
    };
  }, [onLoad]);

  return ref;
}
